import { Injectable } from '@angular/core';
import {
  Feature,
  FEATURE_METADATA,
} from 'src/app/modules/feature-toggle/enums/features.enum';
import { CompanySettingsService } from 'src/app/modules/settings/services/company-settings.service';
import {
  FEATURE_SETTINGS_DEEPLINKS,
  SettingsDeepLink,
  TOUR_KEY_FEATURE,
} from '../config/feature-settings-deeplinks';

export interface PreflightResult {
  /** true = alle benoetigten Settings-Haekchen sind gesetzt, Tour kann direkt starten. */
  ready: boolean;
  /** Die erste Funktion in der Abhaengigkeitskette, deren Settings-Haekchen fehlt. */
  missingFeature?: Feature;
  /** Wo der User das fehlende Haekchen setzt (Route + Anchor). */
  deepLink?: SettingsDeepLink;
}

/**
 * Generischer Settings-Preflight: prueft live, ob die fuer eine Anleitung
 * noetige Funktion in den Company-Settings aktiviert ist. Quelle der Wahrheit
 * fuer "welche Funktion": `requiresFeature` (Backend, Phase 6) bzw. der
 * tourKey-Fallback. Quelle fuer "ist es an": die live geladenen CompanySettings
 * (der Backend-Help-Handler kennt diesen Zustand bewusst nicht).
 */
@Injectable({ providedIn: 'root' })
export class TourPreflightService {
  constructor(private companySettings: CompanySettingsService) {}

  /** Preflight anhand einer Antwort/Quelle (tourKey + optional requiresFeature). */
  preflightForTour(tourKey: string | undefined, requiresFeature?: string): PreflightResult {
    const feature = this.resolveFeature(tourKey, requiresFeature);
    if (feature == null) return { ready: true };
    return this.preflight(feature);
  }

  /** Preflight fuer eine konkrete Funktion (inkl. Abhaengigkeitskette). */
  preflight(feature: Feature): PreflightResult {
    for (const f of this.collectChain(feature)) {
      const meta = FEATURE_METADATA.get(f);
      for (const path of meta?.requiresSettings ?? []) {
        if (this.readFlag(path) !== true) {
          return { ready: false, missingFeature: f, deepLink: FEATURE_SETTINGS_DEEPLINKS[f] };
        }
      }
    }
    return { ready: true };
  }

  /** requiresFeature (Enum-Name-String) bevorzugen, sonst tourKey-Fallback-Map. */
  private resolveFeature(tourKey: string | undefined, requiresFeature?: string): Feature | null {
    if (requiresFeature && (requiresFeature in Feature)) {
      return (Feature as unknown as Record<string, Feature>)[requiresFeature];
    }
    if (tourKey && tourKey in TOUR_KEY_FEATURE) {
      return TOUR_KEY_FEATURE[tourKey];
    }
    return null;
  }

  /** Funktion + transitive Vorgaenger (dependencies) in Reihenfolge "zuerst Vorgaenger". */
  private collectChain(feature: Feature, seen = new Set<Feature>()): Feature[] {
    if (seen.has(feature)) return [];
    seen.add(feature);
    const chain: Feature[] = [];
    for (const dep of FEATURE_METADATA.get(feature)?.dependencies ?? []) {
      chain.push(...this.collectChain(dep, seen));
    }
    chain.push(feature);
    return chain;
  }

  /** Liest einen gepunkteten Pfad wie 'ShiftSettings.ShiftPlanerActive' aus den CompanySettings. */
  private readFlag(path: string): unknown {
    const settings = this.companySettings.CompanySettings as unknown as Record<string, unknown>;
    return path.split('.').reduce<unknown>((obj, key) => {
      if (obj == null || typeof obj !== 'object') return undefined;
      return (obj as Record<string, unknown>)[key];
    }, settings);
  }
}
